import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Video, Upload, LogOut, MapPin, Camera, Cloud, ChevronLeft, CalendarDays,
  ListChecks, FilmIcon, CheckCircle2, RotateCcw, Lock, ArrowLeft, X, Loader,
  PlayCircle, Settings2, Smartphone, UserPlus, MessageCircle,
} from "lucide-react";
import { uploadGameAuto, readVideoDuration } from "@/lib/chunkedUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEGMENT_LABELS = ["Q1", "Q2", "Q3", "Q4", "H1", "H2", "OT"];

/* ─── Header card: GDO profile + assigned cameras + leagues ─── */
const ProfileHeader = ({ profile, onLogout }) => (
  <div data-testid="gdo-profile-header" style={{
    background: 'linear-gradient(135deg, #16191f, #0f1419)',
    border: '1px solid #1e2832', borderRadius: 16,
    padding: '18px 18px 22px', marginBottom: 20,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          background: 'linear-gradient(135deg, #ff6b00, #cc4400)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Video size={22} style={{ color: '#fff' }} />
        </div>
        <div>
          <h1 data-testid="gdo-name" style={{
            fontFamily: "'Outfit', sans-serif", fontSize: 19, fontWeight: 800, color: '#fff',
          }}>{profile.name}</h1>
          <p style={{ fontSize: 11, color: '#ff6b00', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Gameday Operator</p>
        </div>
      </div>
      <button data-testid="gdo-logout-btn" onClick={onLogout} style={{
        background: 'none', border: 'none', color: '#f44336', cursor: 'pointer',
      }}><LogOut size={20} /></button>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12, color: '#bfc7d4' }}>
      <div><strong style={{ color: '#888' }}>Phone:</strong> <span data-testid="gdo-phone">{profile.phone || '—'}</span></div>
      <div><strong style={{ color: '#888' }}>Email:</strong> <span data-testid="gdo-email">{profile.email || '—'}</span></div>
      <div style={{ gridColumn: '1 / span 2' }}>
        <strong style={{ color: '#888' }}><Camera size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />Cameras:</strong>{' '}
        <span data-testid="gdo-cameras">{profile.assigned_cameras || 'No cameras assigned'}</span>
      </div>
      <div style={{ gridColumn: '1 / span 2' }}>
        <strong style={{ color: '#888' }}>League{profile.leagues.length === 1 ? '' : 's'}:</strong>{' '}
        <span data-testid="gdo-leagues">{profile.leagues.length ? profile.leagues.map(l => l.name).join(', ') : 'No leagues assigned'}</span>
      </div>
    </div>
  </div>
);

/* ─── Bubble navigation ─── */
const NavBubble = ({ icon: Icon, label, sublabel, badge, onClick, testid, accent = "#ff6b00" }) => (
  <button data-testid={testid} onClick={onClick} style={{
    width: '100%', background: '#151820', border: `1px solid ${accent}33`,
    borderRadius: 18, padding: '18px 16px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
    transition: 'transform 0.15s, border-color 0.15s',
  }}
  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = accent; }}
  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = accent + '33'; }}
  >
    <div style={{
      width: 48, height: 48, borderRadius: '50%',
      background: `${accent}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon size={22} style={{ color: accent }} />
    </div>
    <div style={{ flex: 1 }}>
      <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', fontFamily: "'Outfit', sans-serif" }}>{label}</p>
      <p style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{sublabel}</p>
    </div>
    {badge != null && (
      <span style={{
        background: accent, color: '#000', fontSize: 12, fontWeight: 800,
        padding: '4px 10px', borderRadius: 12, fontFamily: "'Outfit', sans-serif",
      }}>{badge}</span>
    )}
  </button>
);

/* ─── Per-game upload card (lives inside "This Week's Games") ─── */
const ThisWeekGameCard = ({ schedule, onUpload, onComplete, onReopen, gdoUserId, isYsvAdmin, serverNowIso }) => {
  const game = schedule.linked_game;
  const segments = game?.segments || [];
  const isMultiPart = !!game?.is_multi_part;
  const isLocked = !!game?.multi_part_locked;
  const completedAt = game?.multi_part_completed_at;

  const [showUploader, setShowUploader] = useState(false);
  const [pickedFile, setPickedFile] = useState(null);
  const [segmentChoice, setSegmentChoice] = useState("Full Game");
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null); // "complete" | "reopen" | null
  const fileRef = useRef(null);

  // 48-hour re-open window calculation — server-provided "now" beats device clock.
  const canReopen = (() => {
    if (!completedAt) return false;
    if (isYsvAdmin) return true; // YSV admin always allowed
    try {
      const completed = new Date(completedAt).getTime();
      const now = new Date(serverNowIso).getTime();
      return (now - completed) < 48 * 60 * 60 * 1000;
    } catch { return false; }
  })();

  const hoursUntilLock = (() => {
    if (!completedAt) return null;
    try {
      const completed = new Date(completedAt).getTime();
      const now = new Date(serverNowIso).getTime();
      const remainingMs = 48 * 60 * 60 * 1000 - (now - completed);
      if (remainingMs <= 0) return 0;
      return Math.round(remainingMs / (60 * 60 * 1000));
    } catch { return null; }
  })();

  const usedLabels = segments.map(s => s.label);
  const availableSegments = ["Full Game", ...SEGMENT_LABELS.filter(l => !usedLabels.includes(l))];

  const startUpload = async () => {
    if (!pickedFile) return;
    setUploading(true); setProgress(0);
    try {
      const isFull = segmentChoice === "Full Game";
      // Real duration from the file's metadata — a 0 here breaks the player's
      // timeline (tick marks / quarter markers) for the finished game.
      const durationSeconds = await readVideoDuration(pickedFile);
      await uploadGameAuto(pickedFile, {
        title: `${schedule.home_team_name || ''} vs ${schedule.away_team_name || ''}`,
        description: schedule.location || "",
        team_id: schedule.home_team_id,
        away_team_id: schedule.away_team_id || null,
        opponent: schedule.away_team_name || "",
        game_date: schedule.game_date || "",
        duration_seconds: durationSeconds,
        segment_label: isFull ? null : segmentChoice,
        parent_game_id: (game && !isFull) ? game.id : null,
        schedule_id: schedule.id,
      }, (pct) => setProgress(pct));
      setShowUploader(false); setPickedFile(null); setProgress(0);
      onUpload?.();
    } catch (err) {
      alert(err.response?.data?.detail || err.message || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleComplete = async () => {
    if (!game) return;
    if (!window.confirm("Mark this game complete? You'll have 48 hours to re-open it if you need to add a segment.")) return;
    setBusy("complete");
    try { await onComplete(game.id); } finally { setBusy(null); }
  };

  const handleReopen = async () => {
    if (!game) return;
    setBusy("reopen");
    try { await onReopen(game.id); } finally { setBusy(null); }
  };

  return (
    <div data-testid={`gdo-game-${schedule.id}`} style={{
      background: '#151820', border: '1px solid #1e2832', borderRadius: 14,
      padding: '18px', marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{
          background: '#ff6b00', color: '#fff', fontSize: 11, fontWeight: 800,
          padding: '3px 10px', borderRadius: 12,
        }}>{schedule.game_time || "TBD"}</span>
        <span style={{ fontSize: 12, color: '#888' }}>
          {(schedule.game_date || '').split('T')[0]}
        </span>
        {schedule.location && (
          <span style={{ fontSize: 12, color: '#888', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <MapPin size={11} /> {schedule.location}
          </span>
        )}
      </div>
      <p style={{ fontSize: 15, color: '#fff', fontWeight: 700, marginBottom: 12 }}>
        {schedule.home_team_name} <span style={{ color: '#666' }}>vs</span> {schedule.away_team_name}
      </p>

      {/* Status pills */}
      {isLocked && (
        <div data-testid={`gdo-game-locked-${schedule.id}`} style={{
          background: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.3)',
          borderRadius: 10, padding: '10px 14px', marginBottom: 10,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <CheckCircle2 size={16} style={{ color: '#4caf50' }} />
          <span style={{ color: '#4caf50', fontSize: 13, fontWeight: 700 }}>Game Complete</span>
          {hoursUntilLock != null && hoursUntilLock > 0 && (
            <span style={{ color: '#888', fontSize: 11, marginLeft: 'auto' }}>
              Re-open window: {hoursUntilLock}h left
            </span>
          )}
          {hoursUntilLock === 0 && (
            <span style={{ color: '#888', fontSize: 11, marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Lock size={10} /> Permanently locked
            </span>
          )}
        </div>
      )}

      {/* Segments preview */}
      {isMultiPart && segments.length > 0 && (
        <div data-testid={`gdo-game-segments-${schedule.id}`} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {segments.map(s => (
            <span key={s.id} style={{
              background: '#1e2732', border: '1px solid #2a3a4a', borderRadius: 8,
              padding: '4px 10px', fontSize: 11, color: '#bfc7d4', fontWeight: 600,
            }}>{s.label}</span>
          ))}
        </div>
      )}

      {/* Action area */}
      {isLocked ? (
        <button
          data-testid={`gdo-game-reopen-${schedule.id}`}
          onClick={handleReopen}
          disabled={!canReopen || busy === "reopen"}
          style={{
            width: '100%', padding: '12px', background: canReopen ? 'transparent' : '#1a1a1e',
            border: `1px solid ${canReopen ? '#ff9800' : '#333'}`, borderRadius: 10,
            color: canReopen ? '#ff9800' : '#555', fontSize: 13, fontWeight: 700,
            cursor: canReopen && busy !== "reopen" ? 'pointer' : 'not-allowed',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
          <RotateCcw size={14} /> {busy === "reopen" ? "Reopening…" : (canReopen ? "Re-open Game" : "Game Locked (>48h)")}
        </button>
      ) : showUploader ? (
        <div data-testid={`gdo-uploader-${schedule.id}`}>
          {/* Segment chooser — only show if not first upload OR not full-game already done */}
          {availableSegments.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <p style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Upload as:</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {availableSegments.map(opt => (
                  <button key={opt}
                    data-testid={`seg-pick-${schedule.id}-${opt}`}
                    onClick={() => setSegmentChoice(opt)}
                    style={{
                      padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                      border: segmentChoice === opt ? '1px solid #ff6b00' : '1px solid #2a3a4a',
                      background: segmentChoice === opt ? 'rgba(255,107,0,0.15)' : 'transparent',
                      color: segmentChoice === opt ? '#ff6b00' : '#bfc7d4',
                      cursor: 'pointer',
                    }}>{opt}</button>
                ))}
              </div>
            </div>
          )}
          <input
            ref={fileRef}
            data-testid={`gdo-file-input-${schedule.id}`}
            type="file"
            accept="video/mp4,video/quicktime"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) setPickedFile(f); }}
          />
          {!pickedFile ? (
            <button onClick={() => fileRef.current?.click()} style={{
              width: '100%', padding: '14px', background: '#1e2732', border: '1px dashed #2a3a4a',
              borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 8,
            }}><Upload size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Pick video file</button>
          ) : (
            <div style={{ background: '#1e2732', border: '1px solid #2a3a4a', borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
              <p style={{ fontSize: 12, color: '#fff', wordBreak: 'break-all', marginBottom: 4 }}><strong>{pickedFile.name}</strong></p>
              <p style={{ fontSize: 11, color: '#888' }}>{(pickedFile.size / (1024 * 1024)).toFixed(1)} MB</p>
            </div>
          )}
          {uploading && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ height: 6, background: '#1e2732', borderRadius: 4, overflow: 'hidden' }}>
                <div data-testid={`gdo-upload-progress-${schedule.id}`} style={{ height: '100%', width: `${progress}%`, background: '#00e676', transition: 'width 0.2s' }} />
              </div>
              <p style={{ fontSize: 11, color: '#888', textAlign: 'center', marginTop: 4 }}>Uploading… {progress}%</p>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button onClick={() => { setShowUploader(false); setPickedFile(null); setProgress(0); }} disabled={uploading} style={{
              padding: '12px', background: 'transparent', border: '1px solid #ff6b00', borderRadius: 8,
              color: '#ff6b00', fontSize: 13, fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer',
            }}>Cancel</button>
            <button
              data-testid={`gdo-confirm-upload-${schedule.id}`}
              onClick={startUpload}
              disabled={uploading || !pickedFile}
              style={{
                padding: '12px', background: '#00e676', border: 'none', borderRadius: 8,
                color: '#000', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                opacity: uploading || !pickedFile ? 0.5 : 1,
              }}>{uploading ? `${progress}%…` : 'Upload'}</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: game ? '1fr 1fr' : '1fr', gap: 10 }}>
          <button
            data-testid={`gdo-upload-btn-${schedule.id}`}
            onClick={() => setShowUploader(true)}
            style={{
              padding: '13px 12px', background: game ? 'transparent' : '#00e676',
              border: game ? '1px solid #00e676' : 'none', borderRadius: 10,
              color: game ? '#00e676' : '#000', fontSize: 13, fontWeight: 800, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
            <Cloud size={14} /> {game ? "Add Quarter/Half" : "Upload Game"}
          </button>
          {game && (
            <button
              data-testid={`gdo-complete-btn-${schedule.id}`}
              onClick={handleComplete}
              disabled={busy === "complete"}
              style={{
                padding: '13px 12px', background: '#ff6b00', border: 'none', borderRadius: 10,
                color: '#000', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
              <CheckCircle2 size={14} /> {busy === "complete" ? "Saving…" : "Complete Game"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Equipment-return success modal ─── */
const ShiftCompleteSuccessModal = ({ onClose }) => (
  <div data-testid="shift-complete-success" onClick={onClose} style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 3000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  }}>
    <div style={{
      background: 'linear-gradient(135deg, #1a2a1a, #0f1f0f)', border: '2px solid #4caf50',
      borderRadius: 20, padding: '36px 24px', maxWidth: 420, textAlign: 'center',
    }}>
      <CheckCircle2 size={64} style={{ color: '#4caf50', marginBottom: 14 }} />
      <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 24, fontWeight: 800, color: '#fff', marginBottom: 10 }}>
        GREAT JOB TODAY
      </h2>
      <p style={{ fontSize: 15, color: '#bfc7d4', lineHeight: 1.5, marginBottom: 18 }}>
        Return your equipment to the Hub to receive payment.
      </p>
      <button
        data-testid="shift-complete-success-close"
        onClick={onClose}
        style={{
          padding: '12px 32px', background: '#4caf50', color: '#000', border: 'none',
          borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer',
        }}>Done</button>
    </div>
  </div>
);

/* ─── Confirm "are you sure" modal ─── */
const ConfirmCompleteShiftModal = ({ count, onConfirm, onCancel, busy }) => (
  <div data-testid="confirm-complete-shift" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }} style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 3000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  }}>
    <div style={{ background: '#151820', border: '1px solid #ff6b00', borderRadius: 18, padding: '24px', maxWidth: 380 }}>
      <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 19, fontWeight: 800, color: '#fff', marginBottom: 10 }}>
        Complete shift?
      </h2>
      <p style={{ fontSize: 13, color: '#bfc7d4', lineHeight: 1.5, marginBottom: 18 }}>
        Are you sure you have uploaded all {count} of this week's games? Your League Coordinator and Hub Director will be notified.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <button data-testid="confirm-shift-no" onClick={onCancel} disabled={busy} style={{
          padding: '12px', background: 'transparent', border: '1px solid #888', borderRadius: 10,
          color: '#bfc7d4', fontSize: 13, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer',
        }}>No</button>
        <button data-testid="confirm-shift-yes" onClick={onConfirm} disabled={busy} style={{
          padding: '12px', background: '#00e676', border: 'none', borderRadius: 10,
          color: '#000', fontSize: 13, fontWeight: 800, cursor: busy ? 'wait' : 'pointer',
        }}>{busy ? "Submitting…" : "Yes, complete"}</button>
      </div>
    </div>
  </div>
);

/* ═══════════ Main GDO Portal Page ═══════════ */
const GDOPortalPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null); // full /gdo/portal payload
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("home"); // "home" | "this-week" | "previous" | "unassigned"
  const [confirmShift, setConfirmShift] = useState(false);
  const [shiftBusy, setShiftBusy] = useState(false);
  const [shiftSuccess, setShiftSuccess] = useState(false);

  const getApi = useCallback(() => {
    const token = localStorage.getItem("ysv_token");
    return axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await getApi().get("/gdo/portal");
      setData(r.data);
    } catch (e) {
      // Token expired or wrong role — kick back to login
      navigate("/gdo");
    }
  }, [getApi, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("ysv_token");
    if (!token) { navigate("/gdo"); return; }
    getApi().get("/auth/me").then(r => {
      if (r.data.role !== "gdo" && r.data.role !== "ysv_admin") { navigate("/gdo"); return; }
      setUser(r.data);
      return refresh();
    }).catch(() => navigate("/gdo")).finally(() => setLoading(false));
  }, [getApi, navigate, refresh]);

  const logout = () => {
    localStorage.removeItem("ysv_token");
    localStorage.removeItem("ysv_gdo");
    navigate("/gdo");
  };

  const completeGame = async (gameId) => {
    try {
      await getApi().post(`/games/${gameId}/multi-part/complete`);
      await refresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not complete game");
    }
  };

  const reopenGame = async (gameId) => {
    try {
      await getApi().post(`/games/${gameId}/multi-part/reopen`);
      await refresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not re-open game");
    }
  };

  const submitCompleteShift = async () => {
    setShiftBusy(true);
    try {
      const schedIds = (data?.this_week || []).map(s => s.id);
      await getApi().post("/gdo/complete-shift", { schedule_ids: schedIds });
      setConfirmShift(false);
      setShiftSuccess(true);
      await refresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Submission failed");
    } finally { setShiftBusy(false); }
  };

  if (loading || !data || !user) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size={32} style={{ color: '#ff6b00', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const { profile, this_week: thisWeek, previous_uploads: prevGames, total_uploads: totalUploads, now: serverNow } = data;

  return (
    <div data-testid="gdo-portal-page" style={{ minHeight: '100vh', background: '#0a0a0c', paddingBottom: 60 }}>
      {/* Top breadcrumb — always a "Home" button that returns to the bubbles
          landing page (the post-login GDO home). Logging out happens from the
          profile header's icon. */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #1a1a1e' }}>
        <button
          data-testid="gdo-home-btn"
          onClick={() => setView("home")}
          disabled={view === "home"}
          style={{
            background: view === "home" ? 'transparent' : 'rgba(255,107,0,0.08)',
            border: view === "home" ? '1px solid transparent' : '1px solid rgba(255,107,0,0.4)',
            borderRadius: 999, padding: '6px 14px',
            color: view === "home" ? '#666' : '#ff6b00',
            cursor: view === "home" ? 'default' : 'pointer',
            fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: "'Outfit', sans-serif",
          }}>
          <ChevronLeft size={14} /> HOME
        </button>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '20px' }}>
        {/* ── HOME VIEW: Profile + 3 bubbles + How-To ── */}
        {view === "home" && (
          <>
            <ProfileHeader profile={profile} onLogout={logout} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
              <NavBubble
                testid="bubble-this-week"
                icon={CalendarDays}
                label="This Week's Games"
                sublabel="Upload and complete your assigned games"
                badge={thisWeek.length}
                onClick={() => setView("this-week")}
                accent="#ff6b00"
              />
              <NavBubble
                testid="bubble-previous"
                icon={ListChecks}
                label="Previous Games"
                sublabel="All games you've uploaded"
                badge={totalUploads}
                onClick={() => setView("previous")}
                accent="#00e5ff"
              />
              <NavBubble
                testid="bubble-unassigned"
                icon={FilmIcon}
                label="Upload an Unassigned Game"
                sublabel="Film a game that wasn't on your schedule"
                onClick={() => navigate("/games/new")}
                accent="#4caf50"
              />
            </div>

            {/* ── How-To Videos (disabled until videos exist) ── */}
            <div data-testid="gdo-howto-section">
              <p style={{ fontSize: 11, color: '#ff6b00', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 12 }}>
                How-To Videos
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { icon: Settings2, label: "How to setup tripod" },
                  { icon: Camera, label: "How to record games using XBOT Camera" },
                  { icon: Smartphone, label: "How to download/upload games to YSV" },
                  { icon: UserPlus, label: "How to add a user" },
                  { icon: MessageCircle, label: "Contact your GDC for support" },
                ].map((h, i) => (
                  <div key={i} data-testid={`gdo-howto-${i}`} style={{
                    background: '#0f1217', border: '1px solid #1a1a1e', borderRadius: 12,
                    padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
                    opacity: 0.55, cursor: 'not-allowed',
                  }}>
                    <h.icon size={18} style={{ color: '#666' }} />
                    <p style={{ flex: 1, fontSize: 13, color: '#888', fontWeight: 600 }}>{h.label}</p>
                    <span style={{
                      fontSize: 10, color: '#666', background: '#1a1a1e',
                      padding: '3px 8px', borderRadius: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                    }}>Coming soon</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── THIS WEEK'S GAMES VIEW ── */}
        {view === "this-week" && (
          <div data-testid="this-week-view">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 6 }}>
              This Week's Games
            </h2>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 18 }}>
              {thisWeek.length} game{thisWeek.length === 1 ? '' : 's'} assigned this week
            </p>

            {thisWeek.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Video size={40} style={{ color: '#333', marginBottom: 10 }} />
                <p style={{ color: '#777', fontSize: 14 }}>No games assigned this week</p>
              </div>
            ) : (
              <>
                {thisWeek.map(sched => (
                  <ThisWeekGameCard
                    key={sched.id}
                    schedule={sched}
                    onUpload={refresh}
                    onComplete={completeGame}
                    onReopen={reopenGame}
                    gdoUserId={user.id}
                    isYsvAdmin={user.role === "ysv_admin"}
                    serverNowIso={serverNow}
                  />
                ))}
                <button
                  data-testid="complete-shift-btn"
                  onClick={() => setConfirmShift(true)}
                  style={{
                    width: '100%', padding: '16px', background: '#ff6b00', border: 'none',
                    borderRadius: 12, color: '#000', fontSize: 15, fontWeight: 800, cursor: 'pointer',
                    marginTop: 14, fontFamily: "'Outfit', sans-serif",
                  }}>
                  COMPLETE GAMES
                </button>
              </>
            )}
          </div>
        )}

        {/* ── PREVIOUS GAMES VIEW ── */}
        {view === "previous" && (
          <div data-testid="previous-games-view">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 6 }}>
              Previous Games
            </h2>
            <div data-testid="total-uploads-counter" style={{
              background: 'linear-gradient(135deg, #0f2030, #0a1620)', border: '1px solid #00e5ff44',
              borderRadius: 12, padding: '14px 18px', marginBottom: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: 13, color: '#bfc7d4' }}>Total games uploaded</span>
              <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 28, fontWeight: 800, color: '#00e5ff' }}>{totalUploads}</span>
            </div>

            {prevGames.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <FilmIcon size={40} style={{ color: '#333', marginBottom: 10 }} />
                <p style={{ color: '#777', fontSize: 14 }}>You haven't uploaded any games yet</p>
              </div>
            ) : prevGames.map(g => (
              <div key={g.id} data-testid={`prev-game-${g.id}`} style={{
                background: '#151820', border: '1px solid #1e2832', borderRadius: 12,
                padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(0,229,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <PlayCircle size={20} style={{ color: '#00e5ff' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.title}</p>
                  <p style={{ fontSize: 11, color: '#777' }}>
                    {(g.game_date || g.created_at || '').split('T')[0]}
                    {g.is_multi_part ? ` · ${(g.segments || []).length} parts` : ''}
                    {g.multi_part_locked ? ' · Complete' : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmShift && (
        <ConfirmCompleteShiftModal
          count={thisWeek.length}
          onConfirm={submitCompleteShift}
          onCancel={() => setConfirmShift(false)}
          busy={shiftBusy}
        />
      )}
      {shiftSuccess && <ShiftCompleteSuccessModal onClose={() => { setShiftSuccess(false); setView("home"); }} />}
    </div>
  );
};

export default GDOPortalPage;
